import type { ReactNode } from "react";
import { Link } from "react-router";
import { Avatar, type PersonInfo } from "./Avatar";

export interface PersonResultRowProps {
  person: PersonInfo & { id?: string | null };
  title: string;
  sub?: string | null;
  // Mehrzeiliger Zusatztext unter sub (z.B. ein KI-Begruendungstext) - anders als sub (einzeilig,
  // truncate) wird description NICHT abgeschnitten. Faellt weg, wenn nicht gesetzt.
  description?: ReactNode;
  actions?: ReactNode;
  /** Testanker auf der Zeile (UI_CONVENTIONS → Test anchors). */
  id?: string;
  // Override the default destination. Defaults to `/p/${person.id}` when an id
  // is present. Pass `null` to disable navigation entirely (static row).
  to?: string | null;
  embedded?: boolean;
  // Makes the whole card the choice of this person (the assistant asking which one, an organizational
  // record's draft): a button instead of a link, the chosen card ringed in the accent as on hover.
  onSelect?: () => void;
  selected?: boolean;
}

export function PersonResultRow({
  person,
  title,
  sub,
  description,
  actions,
  to,
  embedded = false,
  id,
  onSelect,
  selected = false,
}: PersonResultRowProps) {
  const href = to === undefined ? (person.id ? `/p/${person.id}` : null) : to;
  // items-start statt items-center, sobald description da ist - sonst zentriert sich der Avatar
  // ueber die ganze Hoehe eines mehrzeiligen Textblocks statt an der Titelzeile auszurichten.
  const baseClass = `flex gap-3 p-3 ${description ? "items-start" : "items-center"} ${embedded ? "bg-page box-flat" : "bg-surface box"}`;
  const titleClass = `truncate text-smycles-body text-ink${href || onSelect ? " transition group-hover:text-ink-accent" : ""}`;

  const inner = (
    <>
      <Avatar person={person} size="lg" />
      <div className="min-w-0 flex-1">
        <div className={titleClass}>{title || "–"}</div>
        {sub ? <div className="truncate text-smycles-meta text-ink-muted">{sub}</div> : null}
        {description ? <div className="mt-1 text-smycles-label leading-snug text-ink-muted">{description}</div> : null}
      </div>
      {actions ? (
        <div
          className="shrink-0"
          onClick={(e) => {
            // stopPropagation alone leaves the wrapping anchor's default
            // navigation in play — preventDefault is what actually cancels it.
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          {actions}
        </div>
      ) : null}
    </>
  );

  if (onSelect) {
    return (
      <button
        id={id}
        type="button"
        aria-pressed={selected}
        onClick={onSelect}
        className={`${baseClass} group w-full cursor-pointer text-left transition hover:ring-accent-decoration/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-ink-ui ${selected ? "smycles-selected ring-2 ring-accent-decoration" : ""}`}
      >
        {inner}
      </button>
    );
  }
  if (href) {
    return (
      <Link
        id={id}
        to={href}
        className={`${baseClass} group cursor-pointer transition hover:ring-accent-decoration/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-ink-ui`}
      >
        {inner}
      </Link>
    );
  }
  return <div id={id} className={baseClass}>{inner}</div>;
}
